import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  BarChart3, 
  Users, 
  Package, 
  Settings, 
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Calendar,
  Clock,
  User,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dataHelpers } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PDVBreadcrumb from '../../components/pdv/PDVBreadcrumb';

const PDVDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [todayStats, setTodayStats] = useState({
    vendasRealizadas: 0,
    faturamento: 0,
    alunosAtendidos: 0
  });
  const [recentSales, setRecentSales] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);

  const company_id = user?.company_id;

  // Carregar dados do dashboard
  useEffect(() => {
    if (company_id) {
      loadDashboardData();
    }
  }, [company_id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Carregar estatísticas do dia
      const stats = await dataHelpers.getTodayStats(company_id);
      setTodayStats({
        vendasRealizadas: stats.salesCount || 0,
        faturamento: stats.todayTotal || 0,
        alunosAtendidos: new Set(stats.sales?.map(s => s.student) || []).size
      });

      // Carregar vendas recentes
      const sales = await dataHelpers.getRecentSales(company_id, 5);
      setRecentSales(sales);

      // Carregar produtos com estoque baixo
      const products = await dataHelpers.getLowStockProducts(company_id, 5);
      setLowStockProducts(products);

    } catch (error) {
      console.error('Erro ao carregar dados do PDV:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    try {
      return new Date(dateString).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '--:--';
    }
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Voltar ao Dashboard',
      icon: ArrowLeft,
      action: () => navigate('/dashboard')
    },    {
      id: 'reports',
      label: 'Relatórios do Dia',
      icon: BarChart3,
      action: () => navigate('/dashboard/pdv/reports')
    },
    {
      id: 'students',
      label: 'Gerenciar Alunos',
      icon: Users,
      action: () => navigate('/dashboard/students')
    },
    {
      id: 'inventory',
      label: 'Estoque',
      icon: Package,
      action: () => navigate('/dashboard/products')
    },
    {
      id: 'settings',
      label: 'Configurações',
      icon: Settings,
      action: () => navigate('/dashboard')
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando PDV...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Menu PDV</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                <Clock className="w-4 h-4 inline mr-1" />
                {new Date().toLocaleTimeString('pt-BR')}
              </div>
              <div className="text-sm text-gray-500">
                <Calendar className="w-4 h-4 inline mr-1" />
                {new Date().toLocaleDateString('pt-BR')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Breadcrumb */}
        <PDVBreadcrumb />
        
        {/* Menu de Navegação */}
        <div className="lg:col-span-1">
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Navegação</h2>
              <div className="space-y-2">
                {menuItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Button
                      key={item.id}
                      variant="ghost"
                      className="w-full justify-start text-left p-3 h-auto"
                      onClick={item.action}
                    >
                      <IconComponent className="w-5 h-5 mr-3 text-gray-500" />
                      <span className="text-gray-700">{item.label}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Estatísticas do Dia */}
          <Card className="mt-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Estatísticas do Dia</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Vendas realizadas</span>
                  <span className="text-2xl font-bold text-gray-900">{todayStats.vendasRealizadas}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Faturamento</span>
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(todayStats.faturamento)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Alunos atendidos</span>
                  <span className="text-2xl font-bold text-blue-600">{todayStats.alunosAtendidos}</span>
                </div>
              </div>

              <Button 
                className="w-full mt-4"
                onClick={loadDashboardData}
                disabled={loading}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Atualizar Dados
              </Button>
            </div>
          </Card>
        </div>

        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Vendas Recentes */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Vendas Recentes</h3>
                <ShoppingCart className="w-5 h-5 text-gray-400" />
              </div>

              {recentSales.length > 0 ? (
                <div className="space-y-4">
                  {recentSales.map((sale, index) => (
                    <div key={sale.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{sale.student || 'Consumidor Final'}</p>
                          <p className="text-sm text-gray-500">{sale.items || 'Venda'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(sale.amount)}</p>
                        <p className="text-sm text-gray-500">{formatTime(sale.time)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhuma venda registrada hoje</p>
                </div>
              )}
            </div>
          </Card>

          {/* Alertas de Estoque */}
          {lowStockProducts.length > 0 && (
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Alertas de Estoque</h3>
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>

                <div className="space-y-3">
                  {lowStockProducts.map((product, index) => (
                    <div 
                      key={product.id || index} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        product.status === 'critical' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
                      }`}
                    >
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className={`text-sm ${
                          product.status === 'critical' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {product.status === 'critical' ? 'Estoque crítico' : 'Estoque baixo'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          product.status === 'critical' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {product.current} unidades
                        </p>
                        <p className="text-sm text-gray-500">{formatCurrency(product.price)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => navigate('/inventory')}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Gerenciar Estoque
                </Button>
              </div>
            </Card>
          )}

          {/* Ações Rápidas */}
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Ações Rápidas</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <Button 
                  variant="outline"
                  className="h-20 flex-col"
                  onClick={() => navigate('/sales/new')}
                >
                  <DollarSign className="w-6 h-6 mb-2 text-green-600" />
                  <span>Nova Venda</span>
                </Button>
                
                <Button 
                  variant="outline"
                  className="h-20 flex-col"
                  onClick={() => navigate('/students/new')}
                >
                  <Users className="w-6 h-6 mb-2 text-blue-600" />
                  <span>Cadastrar Aluno</span>
                </Button>
                
                <Button 
                  variant="outline"
                  className="h-20 flex-col"
                  onClick={() => navigate('/products/new')}
                >
                  <Package className="w-6 h-6 mb-2 text-purple-600" />
                  <span>Novo Produto</span>
                </Button>
                
                <Button 
                  variant="outline"
                  className="h-20 flex-col"
                  onClick={() => navigate('/pdv/reports')}
                >
                  <BarChart3 className="w-6 h-6 mb-2 text-orange-600" />
                  <span>Relatórios</span>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PDVDashboard;
